/**
 * Card "sessione Instagram/Messenger" nella cronologia della scheda cliente:
 * stessa forma della card WhatsApp (nome del contatto, anteprima, bolle
 * espandibili, "Apri in chat" verso il drawer Messaggi sulla conversazione) e
 * stessa chiusura condivisa `TimelineFooter`.
 */
import React, { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import type { ClientMetaSession } from '../../../lib/hooks/useClientMetaConversations';
import { formatSessionRange } from './sessionRange';
import { TimelineFooter } from './TimelineFooter';
import { CardMedia } from './CardMedia';
import { ChatAvatar } from '../../whatsapp2/chat/ChatAvatar';
import { CHIP_NEUTRAL_CLS, formatCompactDate, formatTime } from './cardStyles';

const VISIBLE_BUBBLES = 20;

function sameCalendarDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function relativeDayLabel(date: Date, now: Date): string {
  if (sameCalendarDay(date, now)) return 'oggi';
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (sameCalendarDay(date, yesterday)) return 'ieri';
  return formatCompactDate(date, now);
}

interface ClientMetaSessionCardProps {
  session: ClientMetaSession;
  formatDate: (dateString: string) => string;
}

export const ClientMetaSessionCard: React.FC<ClientMetaSessionCardProps> = ({ session, formatDate }) => {
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const label = session.channel === 'instagram' ? 'Instagram' : 'Messenger';

  const visibleMessages = showAll ? session.messages : session.messages.slice(-VISIBLE_BUBBLES);
  const hiddenCount = session.messages.length - visibleMessages.length;

  const openInChat = () => {
    void import('../../../lib/stores/whatsappDrawerStore').then(({ useWhatsAppDrawerStore }) => {
      useWhatsAppDrawerStore.getState().openMetaConversation(session.channel, session.conversationId);
    });
  };

  const endDate = new Date(session.endAt);
  const startDate = new Date(session.startAt);
  const crossesDays = !sameCalendarDay(startDate, endDate);

  return (
    <div className="group relative mb-3 rounded-lg border border-[rgba(55,53,47,0.16)] bg-white transition-colors hover:border-[rgba(55,53,47,0.26)] dark:border-[#3F4447] dark:bg-[#2F3437] dark:hover:border-[#565D61]">
      <div className="min-w-0 p-3">
        <button onClick={() => setExpanded((v) => !v)} aria-expanded={expanded} className="block w-full text-left">
          {/* Come WhatsApp: siamo nella scheda del contatto, apre con quello che e'
              stato detto, non col nome di chi gia' stiamo guardando. */}
          <CardMedia media={<ChatAvatar name={session.contactName} imageUrl={session.profilePicUrl || undefined} size="sm" />}>
            <div
              className="truncate text-sm font-medium text-[#37352F] dark:text-white"
              title={expanded ? session.contactName : session.lastPreview || session.contactName}
            >
              {expanded ? session.contactName : session.lastPreview || session.contactName}
            </div>
            <div className="mt-0.5 truncate text-xs text-[#787774] dark:text-white/50">
              {session.messageCount} {session.messageCount === 1 ? 'messaggio' : 'messaggi'}
              {session.messageCount > 1 && ` · ${session.inboundCount} ricevuti, ${session.outboundCount} inviati`}
            </div>
          </CardMedia>

          <TimelineFooter
            tipo={label}
            accent="conversazione"
            orario={formatSessionRange(session.startAt, session.endAt)}
            titoloData={crossesDays ? `${formatDate(session.startAt)} → ${formatDate(session.endAt)}` : formatDate(session.endAt)}
            expanded={expanded}
            left={
              session.contactHandle ? (
                <span className={`${CHIP_NEUTRAL_CLS} max-w-[180px] truncate`}>{session.contactHandle}</span>
              ) : undefined
            }
          />
        </button>

        {expanded && (
          <>
            <div className="mt-2.5 flex flex-col gap-1.5">
              {visibleMessages.map((message) => {
                const isOut = message.direction === 'outbound';
                const timeLabel = crossesDays
                  ? `${relativeDayLabel(message.timestamp, new Date())} ${formatTime(message.timestamp)}`
                  : formatTime(message.timestamp);
                return (
                  <div
                    key={message.id}
                    className={`max-w-[78%] px-2.5 py-1.5 text-[12.5px] leading-snug text-[#37352F] dark:text-white/90 ${
                      isOut
                        ? 'self-end rounded-[10px_10px_3px_10px] bg-[rgba(35,131,226,0.10)] dark:bg-[#2383E2]/20'
                        : 'self-start rounded-[10px_10px_10px_3px] bg-[rgba(55,53,47,0.05)] dark:bg-white/[0.06]'
                    }`}
                  >
                    <span className="whitespace-pre-wrap break-words">{message.content}</span>
                    <div className={`mt-0.5 text-[10px] text-[#9B9A97] dark:text-white/40 ${isOut ? 'text-right' : ''}`}>{timeLabel}</div>
                  </div>
                );
              })}
            </div>

            <div className="mt-2.5 flex items-center justify-between border-t border-[rgba(55,53,47,0.09)] pt-2 dark:border-[#3F4447]">
              {hiddenCount > 0 ? (
                <button onClick={() => setShowAll(true)} className="text-[11px] text-[#787774] hover:text-[#37352F] dark:text-white/50 dark:hover:text-white">
                  Mostra altri {hiddenCount} messaggi
                </button>
              ) : (
                <span />
              )}
              <button onClick={openInChat} className="inline-flex items-center gap-1 text-[11px] text-[#787774] hover:text-[#37352F] dark:text-white/50 dark:hover:text-white">
                <ExternalLink size={12} />
                Apri in {label}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
